import { useState } from "react";

// The query fields the gate reads (a TanStack Query result carries all three).
export type MountGateQuery = { data: unknown; isFetching: boolean; isFetchedAfterMount: boolean };

// useMountGate holds a Review queue's ACTS while that queue was served from
// cache at mount and its mount fetch has not settled yet (0051 S1a, F3-2): a
// cached item may already be stale, and the founder should not act on it
// before the fresh list lands.
//
// At the first render for a productKey it latches "served from cache with a
// fetch in flight" (TanStack Query reports the mount fetch on that first
// render already); a new productKey latches again (Review is not remounted
// between products). The hold lifts on the first fetch to settle after the
// observer mounted, success OR error (isFetchedAfterMount), so a failed mount
// fetch releases it too. Once lifted it never comes back for that product: a
// refetch the founder's own save starts, or a window focus, never holds (the
// server's stale refusal and its line cover those). A mount with no fetch in
// flight (a fresh cache under a non-zero staleTime, a cold mount) never holds.
export function useMountGate(query: MountGateQuery, productKey: string): boolean {
  const servedFromCacheWhileFetching = query.data !== undefined && query.isFetching;
  const [latch, setLatch] = useState(() => ({ productKey, held: servedFromCacheWhileFetching }));
  let held = latch.held;
  if (latch.productKey !== productKey) {
    held = servedFromCacheWhileFetching;
    setLatch({ productKey, held });
  }
  if (held && query.isFetchedAfterMount) {
    // Released for good: the latch drops, so nothing can re-arm it for this
    // product.
    held = false;
    setLatch({ productKey, held: false });
  }
  return held;
}
